// Führt die Datensätze aus `uebergabeprotokolle` mit den lokal auf diesem Gerät
// gespeicherten Entwürfen zu GENAU EINER Zeile je Protokoll-Kennung zusammen.
//
// Fix-Runde 1 (Review nach Task 1): dieselbe Übergabe erschien vorher zweimal — als
// Banner-Zeile "wartet auf Upload" (aus listeOffene()) UND als eigene, parteienlose
// Karte "In Erfassung" (aus ladeProtokolle()) — weil beide Quellen unabhängig
// voneinander gerendert wurden und die Kopfzeile beim Anlegen nur die Bezugsfelder
// trägt, keine Parteien. Diese Datei ist die reine Zusammenführungs-Logik dafür,
// getrennt von UebergabeListe.tsx testbar (kein Netzwerk, kein IndexedDB).
import type { Art, Partei, ProtokollStatus } from './typen';
import type { LokalerEntwurf } from './lokal';

export interface ObjektRef { id: string; bezeichnung: string }
export interface EinheitRef { id: string; bezeichnung: string }

/** Eine Zeile aus `uebergabeprotokolle`, wie UebergabeListe.tsx sie per Join lädt. */
export interface DatensatzZeile {
  id: string;
  art: Art;
  status: ProtokollStatus;
  durchgefuehrt_am: string;
  parteien: Partei[];
  pdf_path: string | null;
  /** Gesetzt, sobald das PDF im Aktenschrank (Google Drive) liegt. */
  drive_file_id: string | null;
  mail_versendet_am: string | null;
  erfasst_von: string | null;
  objekt: ObjektRef | null;
  einheit: EinheitRef | null;
}

export type Zeilenzustand =
  | 'lokal_in_bearbeitung'   // Datensatz UND lokaler Entwurf vorhanden — Entwurf ist aktueller
  | 'wartet_auf_upload'      // NUR lokaler Entwurf (noch keine Kopfzeile, z. B. kein Netz beim Anlegen)
  | 'in_erfassung_fremd'     // Datensatz "in_erfassung", aber kein lokaler Entwurf auf DIESEM Gerät
  | 'abgeschlossen'
  | 'abgebrochen';

export interface ZusammengefuehrteZeile {
  id: string;
  zustand: Zeilenzustand;
  art: Art;
  durchgefuehrt_am: string;
  parteien: Partei[];
  pdf_path: string | null;
  drive_file_id: string | null;
  mail_versendet_am: string | null;
  erfasst_von: string | null;
  objekt: ObjektRef | null;
  einheit: EinheitRef | null;
  /** Für "Weiter bearbeiten"/"Jetzt senden" — gesetzt, wann immer auf DIESEM Gerät ein lokaler Entwurf existiert. */
  entwurf: LokalerEntwurf | null;
  /** Für "Abbrechen" (braucht die DB-Kennung) — gesetzt, wann immer eine Kopfzeile existiert. */
  hatDatensatz: boolean;
  /**
   * Fix-Runde 2 (kritischer Fund): true, wenn ein lokaler Entwurf existiert, DER
   * DATENSATZ ABER SCHON ENDGÜLTIG IST (abgeschlossen ODER abgebrochen — z. B. weil
   * loescheEntwurf nach einem Serverabschluss/-abbruch auf einem anderen Gerät
   * fehlschlug). In diesem Fall darf die Liste NICHT "Weiter bearbeiten"/"Jetzt senden"
   * anbieten (der Datensatz hat das letzte Wort, ein Senden würde ohnehin an der
   * Einfrieren-Migration scheitern) — stattdessen nur "Lokalen Entwurf verwerfen".
   */
  entwurfVeraltet: boolean;
}

/**
 * Ein Protokoll ist endgültig (der Server hat das letzte Wort), wenn es abgeschlossen
 * ODER abgebrochen ist — beide Zustände sind vom DB-Trigger gegen jede weitere
 * Änderung gesperrt (s. Migration 2026-07-26-uebergabe-einfrieren.sql, Fix-Runde 2).
 * Zentral hier definiert, damit die Zusammenführung UND die Server-Prüfungen in
 * UebergabeListe.tsx/UebergabeAssistent.tsx nicht auseinanderlaufen können — genau das
 * war die Ursache des Fix-Runde-2-Fundes: der Client kannte "abgebrochen" nirgends.
 */
export function istEndgueltig(status: ProtokollStatus | null | undefined): boolean {
  return status === 'abgeschlossen' || status === 'abgebrochen';
}

function bestimmeZustand(datensatzStatus: ProtokollStatus | null, hatEntwurf: boolean): Zeilenzustand {
  if (!datensatzStatus) return 'wartet_auf_upload';
  if (datensatzStatus === 'abgeschlossen') return 'abgeschlossen';
  if (datensatzStatus === 'abgebrochen') return 'abgebrochen';
  return hatEntwurf ? 'lokal_in_bearbeitung' : 'in_erfassung_fremd';
}

/**
 * Führt Datensätze und lokale Entwürfe zu genau einer Zeile je Kennung zusammen. Bei
 * "beide vorhanden" stammen Art/Zeitpunkt/Parteien aus dem lokalen Entwurf (der ist
 * aktueller als die beim Anlegen geschriebene Kopfzeile) — Objekt-/Einheit-NAMEN
 * kommen dagegen immer aus dem Datensatz (der Entwurf kennt nur die IDs, keine
 * aufgelösten Namen), ebenso die serverseitig verwalteten Felder pdf_path/
 * mail_versendet_am (die trägt ein lokaler Entwurf laut Design nie, s. abschluss.ts).
 */
export function fuehreZeilenZusammen(
  datensaetze: DatensatzZeile[],
  entwuerfe: LokalerEntwurf[],
): ZusammengefuehrteZeile[] {
  const datensatzMap = new Map(datensaetze.map(d => [d.id, d]));
  const entwurfMap = new Map(entwuerfe.map(e => [e.id, e]));
  const ids = new Set<string>([...datensatzMap.keys(), ...entwurfMap.keys()]);

  const zeilen: ZusammengefuehrteZeile[] = [];
  for (const id of ids) {
    const d = datensatzMap.get(id) ?? null;
    const e = entwurfMap.get(id) ?? null;
    const inhalt = e ? e.protokoll : d;
    if (!inhalt) continue; // unerreichbar: id stammt aus mindestens einer der beiden Maps
    zeilen.push({
      id,
      zustand: bestimmeZustand(d?.status ?? null, !!e),
      art: inhalt.art,
      durchgefuehrt_am: inhalt.durchgefuehrt_am,
      parteien: inhalt.parteien,
      pdf_path: d?.pdf_path ?? null,
      drive_file_id: d?.drive_file_id ?? null,
      mail_versendet_am: d?.mail_versendet_am ?? null,
      erfasst_von: d?.erfasst_von ?? e?.protokoll.erfasst_von ?? null,
      objekt: d?.objekt ?? null,
      einheit: d?.einheit ?? null,
      entwurf: e,
      hatDatensatz: !!d,
      entwurfVeraltet: !!e && istEndgueltig(d?.status ?? null),
    });
  }

  zeilen.sort((a, b) => b.durchgefuehrt_am.localeCompare(a.durchgefuehrt_am));
  return zeilen;
}
